import { SocketClientMessage } from '@okletsplay/core';
import { useSocketSubscriber } from 'app/hooks/socket';
import { useFetchData } from 'app/hooks/store';
import { Identifier } from 'app/interfaces';
import { MatchEntity } from 'app/interfaces/entity';
import { CreateMatchModal } from 'modules/matches/components/CreateMatchModal';
import { MatchModal } from 'modules/matches/components/MatchModal';
import { MatchLeaderboardModal } from 'modules/matches/components/MatchLeaderboardModal';
import { MatchResultModal } from 'modules/matches/components/MatchResultModal';
import { WinningAnimationDialog } from 'modules/matches/components/WinningAnimationDialog/WinningAnimationDialog';
import { MATCH_ID_QUERY_KEY } from 'app/constants';
import { useMatchModal } from 'modules/matches/hooks/use-match-modal';
import { getMatchById, updateMatch } from 'modules/matches/store/current-match/repository';
import { currentMatchSelector } from 'modules/matches/store/current-match/selectors';
import React, { FC, PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import { modalsReducers } from 'app/store/modal/slice';
import { ModalName } from 'app/enums';
import { Backdrop } from 'shared/components/Backdrop';
import { useMatchResult } from 'modules/matches/hooks/use-match-result';
import { GameActivityPaymentProvider } from 'modules/game-payments/providers';
import { GameActivityType } from 'shared/enums';

export const MatchUrlQueryDetector: FC<PropsWithChildren<unknown>> = () => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const match: MatchEntity = useSelector(currentMatchSelector.match);
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const matchId = useMemo(() => searchParams.get(MATCH_ID_QUERY_KEY), [searchParams]);

  const { openMatchModal, closeMatchModal } = useMatchModal();

  const actions = useMemo(() => (matchId ? [getMatchById(matchId)] : []), [matchId]);
  const { loading, fetch } = useFetchData<MatchEntity>(actions, { fetchOnInit: false });

  const fetchAndCloseIfError = useCallback(
    (isChangeLoadingState: boolean) =>
      fetch(isChangeLoadingState).catch(() => {
        closeMatchModal();
      }),
    [fetch, closeMatchModal]
  );

  const { currentPlayerWinnings } = useMatchResult(match);

  const onCloseWinningAnimationModal = useCallback(() => {
    dispatch(updateMatch(match?.id));
    dispatch(modalsReducers.openModal({ name: ModalName.MatchResult }));
  }, [dispatch, match?.id]);

  const subscriptionHandler = useCallback(
    (identifier: Identifier) => {
      if (identifier !== matchId) return;

      fetchAndCloseIfError(false);
    },
    [fetchAndCloseIfError, matchId]
  );

  const subscriptions = useMemo(
    () => [
      {
        action: SocketClientMessage.Match,
        handler: subscriptionHandler,
      },
    ],
    [subscriptionHandler]
  );

  useSocketSubscriber(subscriptions);

  useEffect(() => {
    if (!isEmpty(match) && matchId) {
      openMatchModal();
    }
  }, [openMatchModal, match, matchId]);

  useEffect(() => {
    if (matchId) {
      fetchAndCloseIfError(true);
    }
  }, [matchId, fetchAndCloseIfError]);

  if (loading) {
    return <Backdrop />;
  }

  const content = (
    <>
      <MatchModal match={match} onClose={closeMatchModal} />
      <MatchResultModal match={match} onClose={closeMatchModal} />
      <MatchLeaderboardModal match={match} onClose={closeMatchModal} />
      {!isEmpty(match) && (
        <WinningAnimationDialog
          onClose={onCloseWinningAnimationModal}
          prize={currentPlayerWinnings}
          activity={GameActivityType.Match}
        />
      )}
      <CreateMatchModal />
    </>
  );

  return (
    <GameActivityPaymentProvider gameActivity={match} activityType={GameActivityType.Match}>
      {content}
    </GameActivityPaymentProvider>
  );
};
